import React, { useState } from 'react';
import nodeIcon from '../../assets/NodeIcon.svg';
import alertIcon from '../../assets/AlertIcon.svg';
import healthIcon from '../../assets/health.svg';
import timeIcon from '../../assets/time.svg';
import bleIcon from '../../assets/ble.svg';
import boxIcon from '../../assets/box.svg';
import compIcon from '../../assets/comp.svg';
import userIcon from '../../assets/UserIcon.svg'
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useDashboardData, getAlertBadge, alertLevelToHealth } from '../../hooks/useDashboardData';
import './DashboardPage.css';

const formatNumber = (value, decimals = 1) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(decimals);
};

const DashboardPage = () => {
  const [selectedRegion, setSelectedRegion] = useState('thuwal');
  const {
    kpiData,
    alerts,
    environmentData,
    latestNode,
    chartData,
    footerMetrics,
    isLoading,
  } = useDashboardData(selectedRegion);

  const { healthData, envData, nodeData, bleachingData } = chartData;
  const anomaly = environmentData.sstAnomaly ?? 0;
  const buoyBars = envData.slice(-7).map((p) => p.temp).filter((t) => t != null);
  const maxBar = Math.max(...buoyBars, 1);
  const latestBadge = latestNode ? getAlertBadge(latestNode.crw_alert_level) : null;
  const isHealthy = latestBadge?.text === 'Healthy';

  const severityDot = (severity) => {
    if (severity === 'critical' || severity === 'high') return 'red';
    if (severity === 'warning' || severity === 'medium') return 'orange';
    return 'yellow';
  };

  if (isLoading) {
    return <div style={{ backgroundColor: '#141518', height: '100vh' }}></div>;
  }

  return (
    <div className="dashboard-layout">

      <main className="main-content">
        
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-text">
            <h1>MURJAN Dashboard</h1>
            <p>Real-time coral reef monitoring and operations</p>
          </div>
           <select 
            className="add-node-btn" 
            value={selectedRegion} 
            onChange={(e) => setSelectedRegion(e.target.value)}
            style={{ appearance: 'none', cursor: 'pointer', backgroundColor: 'transparent' }}
          >
            <option value="neom" style={{color: 'black'}}>📍 Northern Region (Tabuk & NEOM)</option>
            <option value="shushah" style={{color: 'black'}}>📍 Shushah Island</option>
            <option value="al-wajh" style={{color: 'black'}}>📍 Al-Wajh & Umluj</option>
            <option value="thuwal" style={{color: 'black'}}>📍 Thuwal & Jeddah</option>
            <option value="southern" style={{color: 'black'}}>📍 Southern Region (Al-Lith to Jazan)</option>
          </select>
        </header>

        {/* Row 1: Top KPIs */}
        <div className="kpi-grid">
          
          <div className="kpi-card">
            <div className="kpi-header">
              <div className="kpi-title"><span><img src={nodeIcon} alt="nodeIcon" /></span> Total Nodes</div>
            </div>
            <p className="kpi-value orange">{kpiData.totalNodes}</p>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <div className="kpi-title"><span><img src={alertIcon} alt="alertIcon" /></span> Active Alerts</div>
            </div>
            <p className="kpi-value orange">{kpiData.activeAlerts}</p>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <div className="kpi-title"><span><img src={healthIcon} alt="healthIcon" /></span> Avg Health Score</div>
            </div>
            <p className="kpi-value green">{kpiData.avgHealth}%</p>
          </div>

        </div>

        {/* --- Row 2: Environmental Observations) --- */}
        <div className="env-grid">
          
          <div className="env-card bg-blue">
            <p className="env-card-title">SATELLITE OBSERVATION (NOAA CRW)</p>
            <div className="temp-display">
              <h2>{environmentData.seaTemp != null ? `${formatNumber(environmentData.seaTemp, 2)}°C` : '—'}</h2>
              <p className="sub-label">SEA SURFACE TEMP</p>
            </div>
            <div className="env-metrics">
              <div>
                <h3 className="orange">
                  {anomaly > 0 ? `+${anomaly.toFixed(1)}°` : `${Number(anomaly).toFixed(1)}°`}
                </h3>
                <p className="sub-label">TEMP ANOMALY</p>
              </div>
            </div>
            <div className="anomaly-bar">
              <div
                className="anomaly-fill"
                style={{
                  left: `${Math.max(0, Math.min(((anomaly + 2) / 4) * 100, 100))}%`,
                  transition: 'left 1s ease-in-out',
                }}
              />
            </div>
            <p className="last-updated" style={{ marginTop: 'auto', paddingTop: '20px', color: 'white' }}>
              {environmentData.regionName} · NOAA ERDDAP
            </p>
          </div>

          <div className="env-card bg-teal">
            <p className="env-card-title">DEGREE HEATING WEEKS</p>
            <div className="temp-display">
              <h2>{environmentData.dhw != null ? formatNumber(environmentData.dhw, 1) : '—'}</h2>
              <p className="sub-label">STRESS INDEX</p>
            </div>
            <div className="env-metrics">
              <div>
                <h3>{environmentData.seaTemp != null ? `${formatNumber(environmentData.seaTemp, 2)}°C` : '—'}</h3>
                <p className="sub-label">REGION SST</p>
              </div>
            </div>
            <div className="bar-chart-placeholder">
              {(buoyBars.length ? buoyBars : [0]).map((temp, i) => (
                <div key={i} className="bar" style={{ height: `${(temp / maxBar) * 90}%` }} />
              ))}
            </div>
            <p className="last-updated">Hourly SST trend (Open-Meteo)</p>
          </div>

          {/* Card 3: Bleaching Stress Level */}
          <div className="env-card bg-dark">
            <p className="env-card-title">BLEACHING STRESS LEVEL</p>
            <div className={`stress-level-indicator ${latestBadge?.cls || 'healthy'}`}>
              <span>{environmentData.alertLevel}</span>
              <strong>{environmentData.dhw != null ? formatNumber(environmentData.dhw, 1) : '—'}</strong>
              <small>DHW</small>
            </div>
            <div className="stress-level-details">
              <div>
                <p>SST</p>
                <strong>{environmentData.seaTemp != null ? `${formatNumber(environmentData.seaTemp, 2)}°C` : '—'}</strong>
              </div>
              <div>
                <p>Anomaly</p>
                <strong className={anomaly > 0 ? 'text-orange' : 'text-green'}>
                  {environmentData.sstAnomaly != null ? `${anomaly > 0 ? '+' : ''}${Number(anomaly).toFixed(1)}°C` : '—'}
                </strong>
              </div>
            </div>
          </div>

          {/* Card 4: Wind & Waves (LIVE OPEN-METEO API) */}
          <div className="env-card bg-dark">
            <p className="env-card-title">WIND & WAVES (LIVE API)</p>
            <div className="wind-section">
               <p className="sub-label">WIND</p>
               <h2 className="wind-val">
                 {environmentData.windSpeed} <span className="unit">Km/h</span> 
                 <span className="direction"><img src={compIcon} alt="compIcon" /> {environmentData.windDirection}°</span>
               </h2>
            </div>
            <div className="wave-section">
               <p className="sub-label">WAVES</p>
               <div className="wave-metrics">
                 <div><p>Height</p><h4>{environmentData.waveHeight} m</h4></div>
                 <div><p>Period</p><h4>{environmentData.wavePeriod} s</h4></div>
                 <div><p>Direction</p><h4>{environmentData.waveDirection}°</h4></div>
               </div>
            </div>
          </div>

        </div>

        {/* --- Row 3: Node Status  --- */}
        <div className="status-grid">
          <div className="status-card border-green">
            <p className="status-title"><span className="dot green"></span> Healthy Nodes</p>
            <h2 className="status-val text-green">{kpiData.healthyCount}</h2>
          </div>
          <div className="status-card border-orange">
            <p className="status-title"><span className="dot orange"></span> Warning Nodes</p>
            <h2 className="status-val text-orange">{kpiData.warningCount}</h2>
          </div>
          <div className="status-card border-red">
            <p className="status-title"><span className="dot red"></span> Critical Nodes</p>
            <h2 className="status-val text-red">{kpiData.criticalCount}</h2>
          </div>
        </div>


                {/* --- Row 4: Large Trend Charts --- */}
        <div className="chart-grid">
          
          {/* Chart 1: Health Score Trend */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Health Score Trend</h3>
              <div className="time-filters">
                <span className="active">7D</span>
              </div>
            </div>
            <div style={{ height: '250px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={healthData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                  <XAxis dataKey="time" stroke="#888" tick={{fontSize: 12}} />
                  <YAxis stroke="#888" tick={{fontSize: 12}} domain={['dataMin - 5', 100]} />
                  <Tooltip contentStyle={{ backgroundColor: '#1A1A1D', border: 'none', borderRadius: '8px', color: '#fff' }} />
                  <Line type="monotone" dataKey="score" stroke="#4CAF50" strokeWidth={3} dot={{ r: 4, fill: '#4CAF50' }} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Environmental Metrics */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Environmental Metrics</h3>
              <div className="time-filters">
                <span className="active">24H</span>
              </div>
            </div>
            <div style={{ height: '250px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={envData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                  <XAxis dataKey="time" stroke="#888" tick={{fontSize: 12}} />
                  <YAxis yAxisId="left" stroke="#888" tick={{fontSize: 12}}  />
                  <YAxis yAxisId="right" orientation="right" stroke="#888" tick={{fontSize: 12}}  />
                  <Tooltip contentStyle={{ backgroundColor: '#1A1A1D', border: 'none', borderRadius: '8px', color: '#fff' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                  <Line yAxisId="left" type="monotone" dataKey="temp" name="SST (°C)" stroke="#EA6C52" strokeWidth={3} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="stress" name="DHW" stroke="#3A5A6E" strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>

        {/* --- Row 5: Second Chart Grid --- */}
        <div className="chart-grid">
          
          {/* Chart 3: Active Nodes Over Time */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Active Nodes Over Time</h3>
              <div className="time-filters">
                <span className="active">YTD</span>
              </div>
            </div>
            <div style={{ height: '250px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={nodeData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                  <XAxis dataKey="month" stroke="#888" tick={{fontSize: 12}} />
                  <YAxis stroke="#888" tick={{fontSize: 12}} />
                  <Tooltip contentStyle={{ backgroundColor: '#1A1A1D', border: 'none', borderRadius: '8px', color: '#fff' }} />
                  <Area type="monotone" dataKey="nodes" stroke="#3bc0c3" fillOpacity={0.3} fill="#3bc0c3" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 4: Bleaching Events */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Bleaching Event Alerts</h3>
              <div className="time-filters">
                <span className="active">YTD</span>
              </div>
            </div>
            <div style={{ height: '250px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bleachingData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }} barSize={30}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                  <XAxis dataKey="month" stroke="#888" tick={{fontSize: 12}} />
                  <YAxis stroke="#888" tick={{fontSize: 12}} allowDecimals={false} />
                  <Tooltip cursor={{fill: '#2A2A30'}} contentStyle={{ backgroundColor: '#1A1A1D', border: 'none', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="alerts" name="Total Alerts" fill="#EA6C52" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* --- Row 6: Alerts & Node Details --- */}
        <div className="bottom-grid">
          
          {/* Recent Alerts List */}
          <div className="alerts-card">
            <h3 className="card-title">Recent Alerts</h3>
            
            <div className="alert-list">
              {alerts.length === 0 ? (
                <p style={{ color: '#888', padding: '12px 0' }}>No active alerts</p>
              ) : (
                alerts.map((alert) => (
                  <div className="alert-item" key={alert.id}>
                    <span className={`dot ${severityDot(alert.severity)}`} />
                    <div className="alert-info">
                      <h4>{alert.nodeName}</h4>
                      <p>{alert.message || 'Alert triggered'}</p>
                    </div>
                    <span className="alert-time">{alert.timeAgo}</span>
                  </div>
                ))
              )}
            </div>
          </div>

                    {/* Specific Node Details */}
          <div className="node-details-card">
            <div className="node-header">
              <div className="node-title-group">
                <span style={{ fontSize: '24px', color: '#EA6C52' }}><img src={nodeIcon} alt="NodeIcon" /></span>
                <div>
                  <h3>{latestNode ? (latestNode.name || 'Unnamed Node') : '—'}</h3>
                  <p>{latestNode?.region || 'Latest Status Update'}</p>
                </div>
              </div>
              <div
                className="node-status-badge"
                style={{
                  backgroundColor: isHealthy ? 'rgba(76, 175, 80, 0.1)' : 'rgba(234, 108, 82, 0.1)',
                  color: isHealthy ? '#4CAF50' : '#EA6C52',
                  borderColor: isHealthy ? '#4CAF50' : '#EA6C52',
                }}
              >
                {latestBadge ? latestBadge.text.toUpperCase() : 'NO DATA'}
              </div>
            </div>
            <div className="node-stats">
              <div className="stat-row">
                <span>Latitude:</span>
                <span>{(latestNode?.latitude ?? latestNode?.Lan) != null ? `${latestNode?.latitude ?? latestNode?.Lan}° N` : '—'}</span>
              </div>
              <div className="stat-row">
                <span>Longitude:</span>
                <span>{(latestNode?.longitude ?? latestNode?.Long) != null ? `${latestNode?.longitude ?? latestNode?.Long}° E` : '—'}</span>
              </div>
              <div className="stat-row">
                <span>Health Score:</span>
                <span className={isHealthy ? 'text-green' : 'text-orange'}>
                  {latestNode ? `${alertLevelToHealth(latestNode.crw_alert_level)}%` : '—'}
                </span>
              </div>
              <div className="stat-row">
                <span>NOAA SST:</span>
                <span>{latestNode?.crw_sst != null ? `${latestNode.crw_sst}°C` : '—'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* --- Row 7: Footer Metrics --- */}
        <div className="footer-grid ">
          
          <div className="footer-metric footer-card">
            <div className="metric-header">
              <span className="metric-icon"><img src={boxIcon} alt="boxIcon" /></span>
              <p>Data Sources</p>
            </div>
            <h2 className="metric-val text-orange">{footerMetrics.dataSources}</h2>
          </div>

          <div className="footer-metric footer-card">
            <div className="metric-header">
              <span className="metric-icon"><img src={timeIcon} alt="timeIcon" /></span>
              <p>Last NOAA Sync</p>
            </div>
            <h2 className="metric-val text-white">{footerMetrics.lastUpdated}</h2>
          </div>

          <div className="footer-metric footer-card">
            <div className="metric-header">
              <span className="metric-icon"><img src={bleIcon} alt="bleIcon" /></span>
              <p>High-Stress Nodes</p>
            </div>
            <h2 className="metric-val text-green">{footerMetrics.bleachingEvents}</h2>
          </div>

          <div className="footer-metric footer-card">
            <div className="metric-header">
              <span className="metric-icon"><img src={userIcon} alt="userIcon" /></span>
              <p>Operators</p>
            </div>
            <h2 className="metric-val text-white">{footerMetrics.operatorsOnline}</h2>
          </div>

        </div>

      </main>
    </div>
  );
};

export default DashboardPage;
